"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import type {
  ActiveCollectible,
  AirResistanceParticle,
  DropPodPhase,
  DropPodRewards,
  DropPodState,
  ThrusterParticle,
  DropPodVariant,
  SkyTheme,
} from "@/types/drop-pod.types"
import { getRandomDropPodVariant } from "@/data/drop-pod-variants"
import { LoadingOverlay } from "@/components/ui/loading-overlay"
import { playDroppodFlySound } from "@/hooks/use-sound-effects"

const MINIGAME_DURATION = 5000 // 5 seconds
const POD_WIDTH = 48
const POD_HEIGHT = 64
const COLLECTIBLE_RADIUS = 12
const POD_HITBOX_WIDTH = 36

const SPACE_END = 0.4
const ATMOSPHERE_END = 0.7
const EXIT_START = 0.85

interface DropPodMinigameProps {
  onComplete: (rewards: DropPodRewards) => void
  /** Accepted for call-site symmetry; the minigame art is hero-agnostic */
  selectedHero: string
}

const isFiniteNumber = (n: number): boolean => Number.isFinite(n) && !Number.isNaN(n)

const usePickupSound = () => {
  const audioRef = useRef<HTMLAudioElement | null>(null)

  useEffect(() => {
    audioRef.current = new Audio("https://hebbkx1anhila5yf.public.blob.vercel-storage.com/minigame-pickup-0EFZISFOzj9JF7aiNeU3sidfdVyqHz.mp3")
    audioRef.current.volume = 0.5
  }, [])

  const play = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0
      audioRef.current.play().catch(() => {})
    }
  }, [])

  return play
}

export function DropPodMinigame({ onComplete }: DropPodMinigameProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const animationRef = useRef<number>(0)
  const startTimeRef = useRef<number>(0)
  const lastTimeRef = useRef<number>(0)

  const [canvasDimensions, setCanvasDimensions] = useState({ width: 0, height: 0 })
  const [hudRewards, setHudRewards] = useState<DropPodRewards>({ coins: 0, lives: 0, freePremiumTowers: 0 })
  const [pulseType, setPulseType] = useState<"lives" | "coins" | "towers" | null>(null)

  // Randomly select a variant on mount
  const [variant] = useState<DropPodVariant>(() => getRandomDropPodVariant())

  const podStateRef = useRef<DropPodState>({
    x: 0.5,
    velocity: 0,
    scale: 1.0,
    y: 0.92,
  })
  const collectiblesRef = useRef<ActiveCollectible[]>([])
  const particlesRef = useRef<ThrusterParticle[]>([])
  const rewardsRef = useRef<DropPodRewards>({ coins: 0, lives: 0, freePremiumTowers: 0 })
  const phaseRef = useRef<DropPodPhase>("dropping")
  const inputRef = useRef<{ left: boolean; right: boolean }>({ left: false, right: false })
  const touchTargetXRef = useRef<number | null>(null)
  const particleIdRef = useRef(0)
  const airParticlesRef = useRef<AirResistanceParticle[]>([])
  const airParticleIdRef = useRef(0)

  const podImageRef = useRef<HTMLImageElement | null>(null)
  const towerImageRef = useRef<HTMLImageElement | null>(null)
  const [imageLoaded, setImageLoaded] = useState(false)
  const [bokehOpacity, setBokehOpacity] = useState(0)

  const playPickupSound = usePickupSound()

  useEffect(() => {
    const updateDimensions = () => {
      const container = containerRef.current
      if (container) {
        const rect = container.getBoundingClientRect()
        const w = Math.max(Math.floor(rect.width), 100)
        const h = Math.max(Math.floor(rect.height), 100)
        if (isFiniteNumber(w) && isFiniteNumber(h)) {
          setCanvasDimensions({ width: w, height: h })
        }
      }
    }

    const timer = setTimeout(updateDimensions, 50)
    window.addEventListener("resize", updateDimensions)
    return () => {
      clearTimeout(timer)
      window.removeEventListener("resize", updateDimensions)
    }
  }, [])

  useEffect(() => {
    const img = new window.Image()
    img.crossOrigin = "anonymous"
    img.onload = () => {
      podImageRef.current = img
      setImageLoaded(true)
    }
    img.src = "/images/droppod.png"

    // Also load the premium tower icon
    const towerImg = new window.Image()
    towerImg.crossOrigin = "anonymous"
    towerImg.onload = () => {
      towerImageRef.current = towerImg
    }
    towerImg.src = "/images/icon-premiumtower.png"
  }, [])

  useEffect(() => {
    collectiblesRef.current = variant.collectibles.map((c) => ({
      ...c,
      y: 1.2,
      collected: false,
    }))
  }, [variant])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "a" || e.key === "A" || e.key === "ArrowLeft") {
        inputRef.current.left = true
      }
      if (e.key === "d" || e.key === "D" || e.key === "ArrowRight") {
        inputRef.current.right = true
      }
    }

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === "a" || e.key === "A" || e.key === "ArrowLeft") {
        inputRef.current.left = false
      }
      if (e.key === "d" || e.key === "D" || e.key === "ArrowRight") {
        inputRef.current.right = false
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    window.addEventListener("keyup", handleKeyUp)
    return () => {
      window.removeEventListener("keydown", handleKeyDown)
      window.removeEventListener("keyup", handleKeyUp)
    }
  }, [])

  // Touch controls for mobile
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const handleTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0]
      const rect = container.getBoundingClientRect()
      touchTargetXRef.current = (touch.clientX - rect.left) / rect.width
    }

    const handleTouchMove = (e: TouchEvent) => {
      const touch = e.touches[0]
      const rect = container.getBoundingClientRect()
      touchTargetXRef.current = (touch.clientX - rect.left) / rect.width
    }

    const handleTouchEnd = () => {
      touchTargetXRef.current = null
    }

    container.addEventListener("touchstart", handleTouchStart)
    container.addEventListener("touchmove", handleTouchMove)
    container.addEventListener("touchend", handleTouchEnd)
    return () => {
      container.removeEventListener("touchstart", handleTouchStart)
      container.removeEventListener("touchmove", handleTouchMove)
      container.removeEventListener("touchend", handleTouchEnd)
    }
  }, [])

  // Mouse controls for desktop - pod follows mouse with delay (no click required)
  const mouseTargetXRef = useRef<number | null>(null)
  const isMouseInContainerRef = useRef(false)
  const isUsingTouchRef = useRef(false)

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    // Track if user is actively using touch - this is more reliable than device detection
    // since many desktops have touch capability but users still use mouse
    const handleTouchStartGlobal = () => {
      isUsingTouchRef.current = true
      // Clear mouse target when switching to touch
      mouseTargetXRef.current = null
    }

    const handleMouseEnter = () => {
      // Skip if user recently used touch
      if (isUsingTouchRef.current) return
      isMouseInContainerRef.current = true
    }

    const handleMouseMove = (e: MouseEvent) => {
      // Skip if user recently used touch
      if (isUsingTouchRef.current) return
      if (!isMouseInContainerRef.current) return
      const rect = container.getBoundingClientRect()
      mouseTargetXRef.current = (e.clientX - rect.left) / rect.width
    }

    const handleMouseLeave = () => {
      isMouseInContainerRef.current = false
      mouseTargetXRef.current = null
    }

    // Listen for touch globally to detect touch usage
    window.addEventListener("touchstart", handleTouchStartGlobal, { passive: true })
    container.addEventListener("mouseenter", handleMouseEnter)
    container.addEventListener("mousemove", handleMouseMove)
    container.addEventListener("mouseleave", handleMouseLeave)

    return () => {
      window.removeEventListener("touchstart", handleTouchStartGlobal)
      container.removeEventListener("mouseenter", handleMouseEnter)
      container.removeEventListener("mousemove", handleMouseMove)
      container.removeEventListener("mouseleave", handleMouseLeave)
    }
  }, [])

  const spawnParticle = useCallback((podX: number, podY: number, scale: number) => {
    const baseSize = 3 + Math.random() * 4
    const particle: ThrusterParticle = {
      id: particleIdRef.current++,
      x: podX + (Math.random() - 0.5) * 0.02,
      y: podY + 0.02 * scale, // Closer to top of pod where engines are
      vx: (Math.random() - 0.5) * 0.002,
      vy: 0.008 + Math.random() * 0.004,
      life: 1,
      maxLife: 1,
      size: baseSize * scale,
      color: Math.random() > 0.5 ? "#ff6b35" : Math.random() > 0.5 ? "#ffa500" : "#ffdd00",
    }
    particlesRef.current.push(particle)

    if (particlesRef.current.length > 50) {
      particlesRef.current.shift()
    }
  }, [])

  // Spawn air resistance particles in a crescent pattern below the pod
  const spawnAirParticles = useCallback((podX: number, podY: number, scale: number) => {
    const particleCount = 3 + Math.floor(Math.random() * 3) // 3-5 particles per frame
    const podHalfWidth = (POD_WIDTH * scale) / canvasDimensions.width / 2
    const podBottomOffset = (POD_HEIGHT * scale) / canvasDimensions.height / 2

    for (let i = 0; i < particleCount; i++) {
      // Random angle along the crescent (0 to π)
      const angle = Math.random() * Math.PI
      // X position: spread across pod width using cos
      const xOffset = Math.cos(angle) * podHalfWidth * 0.9
      // Y offset: crescent shape - edges higher (cos=±1), center lower (cos=0)
      // At edges: closer to pod; at center: slightly below
      const crescentDepth = 0.008 * scale // How deep the crescent dips
      const yOffset = podBottomOffset + crescentDepth * (1 - Math.abs(Math.cos(angle)))

      const particle: AirResistanceParticle = {
        id: airParticleIdRef.current++,
        x: podX + xOffset + (Math.random() - 0.5) * 0.005,
        y: podY - yOffset + (Math.random() - 0.5) * 0.003,
        vx: xOffset * 0.02, // Slight outward spread
        vy: 0.004 + Math.random() * 0.003, // Travel upward (in pod coord system)
        life: 0.6 + Math.random() * 0.3,
        size: 1 + Math.random() * 2,
      }
      airParticlesRef.current.push(particle)
    }

    // Limit pool size
    if (airParticlesRef.current.length > 100) {
      airParticlesRef.current.splice(0, airParticlesRef.current.length - 100)
    }
  }, [canvasDimensions.width, canvasDimensions.height])

  const drawBackground = useCallback(
    (ctx: CanvasRenderingContext2D, progress: number, theme: SkyTheme) => {
      const w = canvasDimensions.width
      const h = canvasDimensions.height

      if (!isFiniteNumber(w) || !isFiniteNumber(h) || w <= 0 || h <= 0) return

      // Helper to parse hex color to RGB
      const hexToRgb = (hex: string) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
        return result
          ? {
              r: parseInt(result[1], 16),
              g: parseInt(result[2], 16),
              b: parseInt(result[3], 16),
            }
          : { r: 0, g: 0, b: 0 }
      }

      // Helper to interpolate between two colors
      const lerpColor = (c1: { r: number; g: number; b: number }, c2: { r: number; g: number; b: number }, t: number) => ({
        r: Math.floor(c1.r + (c2.r - c1.r) * t),
        g: Math.floor(c1.g + (c2.g - c1.g) * t),
        b: Math.floor(c1.b + (c2.b - c1.b) * t),
      })

      if (progress < SPACE_END) {
        // Space phase - use theme colors
        const gradient = ctx.createLinearGradient(0, 0, 0, h)
        gradient.addColorStop(0, theme.spaceTop)
        gradient.addColorStop(1, theme.spaceBottom)
        ctx.fillStyle = gradient
        ctx.fillRect(0, 0, w, h)

        // Stars with theme color
        ctx.fillStyle = theme.starColor
        for (let i = 0; i < theme.starCount; i++) {
          const phi = 1.618033988749
          const starX = ((i * phi * 137.5) % 1) * w
          const starY = (i * phi * 213.7 + progress * h * 0.5) % h
          const sizeSeed = Math.sin(i * 12.9898) * 43758.5453
          const size = 0.5 + (sizeSeed - Math.floor(sizeSeed)) * 2
          ctx.globalAlpha = 0.2 + (Math.sin(i * 7.3) * 0.5 + 0.5) * 0.6
          ctx.fillRect(starX, starY, size, size)
        }
        ctx.globalAlpha = 1
      } else if (progress < ATMOSPHERE_END) {
        // Atmosphere phase - interpolate from space to atmosphere colors
        const atmosphereProgress = (progress - SPACE_END) / (ATMOSPHERE_END - SPACE_END)

        const spaceTop = hexToRgb(theme.spaceTop)
        const spaceBottom = hexToRgb(theme.spaceBottom)
        const atmoTop = hexToRgb(theme.atmosphereTop)
        const atmoBottom = hexToRgb(theme.atmosphereBottom)

        const topColor = lerpColor(spaceTop, atmoTop, atmosphereProgress)
        const bottomColor = lerpColor(spaceBottom, atmoBottom, atmosphereProgress)

        const gradient = ctx.createLinearGradient(0, 0, 0, h)
        gradient.addColorStop(0, `rgb(${topColor.r},${topColor.g},${topColor.b})`)
        gradient.addColorStop(1, `rgb(${bottomColor.r},${bottomColor.g},${bottomColor.b})`)
        ctx.fillStyle = gradient
        ctx.fillRect(0, 0, w, h)

        // Fading stars
        ctx.fillStyle = theme.starColor
        const starCount = Math.floor(theme.starCount * (1 - atmosphereProgress))
        for (let i = 0; i < starCount; i++) {
          const phi = 1.618033988749
          const starX = ((i * phi * 137.5) % 1) * w
          const starY = (i * phi * 213.7 + progress * h * 0.5) % h
          const sizeSeed = Math.sin(i * 12.9898) * 43758.5453
          const baseSize = 0.5 + (sizeSeed - Math.floor(sizeSeed)) * 2
          const size = baseSize * (1 - atmosphereProgress * 0.7)
          ctx.globalAlpha = (0.2 + (Math.sin(i * 7.3) * 0.5 + 0.5) * 0.6) * (1 - atmosphereProgress)
          ctx.fillRect(starX, starY, size, size)
        }
        ctx.globalAlpha = 1
      } else {
        // Sky phase - use theme sky colors
        const gradient = ctx.createLinearGradient(0, 0, 0, h)
        gradient.addColorStop(0, theme.skyTop)
        gradient.addColorStop(0.5, theme.skyMiddle)
        gradient.addColorStop(1, theme.skyBottom)
        ctx.fillStyle = gradient
        ctx.fillRect(0, 0, w, h)

        const skyProgress = (progress - ATMOSPHERE_END) / (1 - ATMOSPHERE_END)
        const cloudFadeIn = Math.min(1, skyProgress * 3)

        // Clouds with theme color and opacity
        const cloudRgb = hexToRgb(theme.cloudColor)
        ctx.fillStyle = `rgba(${cloudRgb.r}, ${cloudRgb.g}, ${cloudRgb.b}, ${theme.cloudOpacity * cloudFadeIn})`
        for (let i = 0; i < 5; i++) {
          const cloudX = ((i * 200 + progress * 40) % (w + 200)) - 100
          const cloudY = 50 + i * 40
          const cloudWidth = 60 + (i % 3) * 30
          const cloudHeight = 15 + (i % 2) * 5

          ctx.beginPath()
          ctx.ellipse(cloudX, cloudY, cloudWidth, cloudHeight, 0, 0, Math.PI * 2)
          ctx.fill()
          ctx.beginPath()
          ctx.ellipse(cloudX + cloudWidth * 0.4, cloudY - 3, cloudWidth * 0.6, cloudHeight * 0.8, 0, 0, Math.PI * 2)
          ctx.fill()
          ctx.beginPath()
          ctx.ellipse(cloudX - cloudWidth * 0.3, cloudY + 2, cloudWidth * 0.5, cloudHeight * 0.7, 0, 0, Math.PI * 2)
          ctx.fill()
        }

        // Optional fog overlay for foggy variant
        if (theme.fogColor && theme.fogOpacity) {
          const fogRgb = hexToRgb(theme.fogColor)
          ctx.fillStyle = `rgba(${fogRgb.r}, ${fogRgb.g}, ${fogRgb.b}, ${theme.fogOpacity * cloudFadeIn})`
          ctx.fillRect(0, 0, w, h)
        }
      }
    },
    [canvasDimensions.width, canvasDimensions.height],
  )

  const drawCollectible = useCallback(
    (ctx: CanvasRenderingContext2D, collectible: ActiveCollectible) => {
      const x = collectible.x * canvasDimensions.width
      const y = collectible.y * canvasDimensions.height
      const radius = COLLECTIBLE_RADIUS

      if (!isFiniteNumber(x) || !isFiniteNumber(y)) return

      ctx.save()

      switch (collectible.type) {
        case "coin":
          ctx.fillStyle = "#ffd700"
          ctx.beginPath()
          ctx.arc(x, y, radius, 0, Math.PI * 2)
          ctx.fill()
          ctx.strokeStyle = "#b8860b"
          ctx.lineWidth = 2
          ctx.stroke()
          ctx.fillStyle = "#b8860b"
          ctx.font = "bold 12px sans-serif"
          ctx.textAlign = "center"
          ctx.textBaseline = "middle"
          ctx.fillText("$", x, y)
          break

        case "life": {
          ctx.fillStyle = "#ff4444"
          ctx.beginPath()
          const heartSize = radius * 1.2
          ctx.moveTo(x, y + heartSize * 0.3)
          ctx.bezierCurveTo(
            x,
            y - heartSize * 0.3,
            x - heartSize,
            y - heartSize * 0.3,
            x - heartSize,
            y + heartSize * 0.1,
          )
          ctx.bezierCurveTo(x - heartSize, y + heartSize * 0.6, x, y + heartSize, x, y + heartSize)
          ctx.bezierCurveTo(x, y + heartSize, x + heartSize, y + heartSize * 0.6, x + heartSize, y + heartSize * 0.1)
          ctx.bezierCurveTo(x + heartSize, y - heartSize * 0.3, x, y - heartSize * 0.3, x, y + heartSize * 0.3)
          ctx.fill()
          break
          }

        case "premium-tower":
          // Use the premium tower icon if loaded
          if (towerImageRef.current) {
            const size = radius * 2.5
            ctx.drawImage(towerImageRef.current, x - size / 2, y - size / 2, size, size)
          } else {
            // Fallback to purple star if image not loaded
            ctx.fillStyle = "#9b59b6"
            ctx.beginPath()
            ctx.moveTo(x, y - radius)
            for (let i = 0; i < 5; i++) {
              const angle = (i * 4 * Math.PI) / 5 - Math.PI / 2
              const r = i % 2 === 0 ? radius : radius * 0.5
              ctx.lineTo(x + Math.cos(angle) * r, y + Math.sin(angle) * r)
            }
            ctx.closePath()
            ctx.fill()
            ctx.strokeStyle = "#8e44ad"
            ctx.lineWidth = 2
            ctx.stroke()
          }
          break
      }

      ctx.restore()
    },
    [canvasDimensions.width, canvasDimensions.height],
  )

  const drawParticles = useCallback(
    (ctx: CanvasRenderingContext2D) => {
      const { width, height } = canvasDimensions
      ctx.globalAlpha = 0.8
      for (const p of particlesRef.current) {
        const x = p.x * width
        // This makes particles render in same coord system as pod
        const y = (1 - p.y) * height
        if (!isFiniteNumber(x) || !isFiniteNumber(y)) continue
        ctx.fillStyle = p.color
        ctx.beginPath()
        ctx.arc(x, y, p.size * p.life, 0, Math.PI * 2)
        ctx.fill()
      }
      ctx.globalAlpha = 1
    },
    [canvasDimensions],
  )

  // Draw air resistance particles (white, below pod)
  const drawAirParticles = useCallback(
    (ctx: CanvasRenderingContext2D) => {
      const { width, height } = canvasDimensions
      for (const p of airParticlesRef.current) {
        const x = p.x * width
        const y = (1 - p.y) * height
        if (!isFiniteNumber(x) || !isFiniteNumber(y)) continue
        ctx.globalAlpha = p.life * 0.7
        ctx.fillStyle = "#ffffff"
        ctx.beginPath()
        ctx.arc(x, y, p.size, 0, Math.PI * 2)
        ctx.fill()
      }
      ctx.globalAlpha = 1
    },
    [canvasDimensions],
  )

  const drawPod = useCallback(
    (ctx: CanvasRenderingContext2D, pod: DropPodState) => {
      const x = pod.x * canvasDimensions.width
      const y = (1 - pod.y) * canvasDimensions.height
      const scale = pod.scale
      const w = POD_WIDTH * scale
      const h = POD_HEIGHT * scale

      if (!isFiniteNumber(x) || !isFiniteNumber(y)) return

      if (podImageRef.current) {
        ctx.drawImage(podImageRef.current, x - w / 2, y - h / 2, w, h)
      } else {
        ctx.fillStyle = "#8b4513"
        ctx.fillRect(x - w / 2, y - h / 2, w, h)
      }
    },
    [canvasDimensions.width, canvasDimensions.height],
  )

  const drawProgressBar = useCallback(
    (ctx: CanvasRenderingContext2D, progress: number) => {
      const padding = 10
      const barWidth = 100
      const barHeight = 6
      const barX = canvasDimensions.width - barWidth - padding
      const barY = 14

      if (!isFiniteNumber(barX)) return

      ctx.fillStyle = "rgba(0,0,0,0.5)"
      ctx.fillRect(barX, barY, barWidth, barHeight)
      ctx.fillStyle = "#ffffff"
      ctx.fillRect(barX, barY, barWidth * progress, barHeight)
    },
    [canvasDimensions.width],
  )

  const gameLoop = useCallback(
    (timestamp: number) => {
      if (!startTimeRef.current) {
        startTimeRef.current = timestamp
        lastTimeRef.current = timestamp
      }

      const elapsed = timestamp - startTimeRef.current
      const deltaTime = Math.min(timestamp - lastTimeRef.current, 32)
      lastTimeRef.current = timestamp

      const canvas = canvasRef.current
      const ctx = canvas?.getContext("2d")

      if (
        !ctx ||
        !canvas ||
        !isFiniteNumber(canvasDimensions.width) ||
        !isFiniteNumber(canvasDimensions.height) ||
        canvasDimensions.width <= 0 ||
        canvasDimensions.height <= 0
      ) {
        animationRef.current = requestAnimationFrame(gameLoop)
        return
      }

      const phase = phaseRef.current
      const pod = podStateRef.current

      if (phase === "dropping") {
        const progress = Math.min(elapsed / MINIGAME_DURATION, 1)
        const speedMultiplier = 1 + progress * 2

        if (touchTargetXRef.current !== null) {
          // Touch controls (mobile) - more responsive
          const targetX = touchTargetXRef.current
          const diff = targetX - pod.x
          pod.x += diff * 0.15
        } else if (mouseTargetXRef.current !== null) {
          // Mouse controls (desktop) - pod follows mouse with smooth delay
          // Clamp target to same bounds as keyboard (0.1 to 0.9)
          const targetX = Math.max(0.1, Math.min(0.9, mouseTargetXRef.current))
          const diff = targetX - pod.x
          pod.x += diff * 0.06 // Smooth lerp for delayed following effect
        } else {
          // Keyboard controls
          if (inputRef.current.left) {
            pod.velocity -= 0.004
          }
          if (inputRef.current.right) {
            pod.velocity += 0.004
          }
          pod.velocity *= 0.95
          pod.x += pod.velocity
        }
        pod.x = Math.max(0.1, Math.min(0.9, pod.x))

        if (progress < EXIT_START) {
          pod.scale = 1.0 + progress * 1.4
          pod.y = 0.92 - progress * 0.42
        } else {
          const exitProgress = (progress - EXIT_START) / (1 - EXIT_START)
          const easeIn = exitProgress * exitProgress * exitProgress

          pod.scale = 2.19 + exitProgress * 0.8
          pod.y = 0.5 - easeIn * 0.8

          setBokehOpacity(Math.min(1, exitProgress * 1.5))
        }

        let rewardsChanged = false

        for (const c of collectiblesRef.current) {
          if (c.collected) continue

          if (progress >= c.spawnTime && c.y > 1) {
            c.y = 1.0
          }

          if (c.y <= 1) {
            c.y -= 0.012 * speedMultiplier * (deltaTime / 16)
          }

          if (c.y > 0 && c.y < 1) {
            const dx = Math.abs(c.x - pod.x) * canvasDimensions.width
            const collectibleScreenY = c.y * canvasDimensions.height
            const podScreenY = (1 - pod.y) * canvasDimensions.height
            const dy = Math.abs(collectibleScreenY - podScreenY)
            const hitDistance = POD_HITBOX_WIDTH / 2 + COLLECTIBLE_RADIUS

            if (dx < hitDistance && dy < hitDistance * 1.5) {
              c.collected = true
              rewardsChanged = true
              playPickupSound()
              switch (c.type) {
                case "coin":
                  rewardsRef.current.coins += c.value
                  setPulseType("coins")
                  setTimeout(() => setPulseType(null), 200)
                  break
                case "life":
                  rewardsRef.current.lives += c.value
                  setPulseType("lives")
                  setTimeout(() => setPulseType(null), 200)
                  break
                case "premium-tower":
                  rewardsRef.current.freePremiumTowers += c.value
                  setPulseType("towers")
                  setTimeout(() => setPulseType(null), 200)
                  break
              }
            }
          }
        }

        if (rewardsChanged) {
          setHudRewards({ ...rewardsRef.current })
        }

        const particleChance = progress >= EXIT_START ? 0.1 : 0.3
        if (Math.random() > particleChance) {
          spawnParticle(pod.x, pod.y, pod.scale)
        }

        particlesRef.current = particlesRef.current.filter((p) => {
          p.x += p.vx
          p.y += p.vy
          p.life -= 0.03
          return p.life > 0
        })

        // Spawn and update air resistance particles
        spawnAirParticles(pod.x, pod.y, pod.scale)
        airParticlesRef.current = airParticlesRef.current.filter((p) => {
          p.x += p.vx
          p.y += p.vy
          p.life -= 0.06
          return p.life > 0
        })

        ctx.clearRect(0, 0, canvas.width, canvas.height)
        drawBackground(ctx, progress, variant.skyTheme)

        drawAirParticles(ctx)
        drawParticles(ctx)

        for (const c of collectiblesRef.current) {
          if (!c.collected && c.y > -0.1 && c.y < 1.1) {
            drawCollectible(ctx, c)
          }
        }

        drawPod(ctx, pod)
        drawProgressBar(ctx, progress)

        if (progress >= 1) {
          phaseRef.current = "landed"
          onComplete(rewardsRef.current)
          return
        }
      }

      animationRef.current = requestAnimationFrame(gameLoop)
    },
    [
      canvasDimensions.width,
      canvasDimensions.height,
      drawBackground,
      drawCollectible,
      drawProgressBar,
      drawParticles,
      drawAirParticles,
      drawPod,
      onComplete,
      spawnParticle,
      spawnAirParticles,
      playPickupSound,
      variant.skyTheme,
    ],
  )

  useEffect(() => {
    if (!imageLoaded || canvasDimensions.width <= 0 || canvasDimensions.height <= 0) return

    // Play droppod fly sound when minigame starts
    playDroppodFlySound()

    animationRef.current = requestAnimationFrame(gameLoop)
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [imageLoaded, gameLoop, canvasDimensions.width, canvasDimensions.height])

  return (
    <div
      ref={containerRef}
      id="drop-pod-minigame-container"
      className="absolute inset-0 flex items-center justify-center bg-black z-50"
    >
      {canvasDimensions.width > 0 && canvasDimensions.height > 0 && (
        <canvas
          ref={canvasRef}
          id="drop-pod-canvas"
          width={canvasDimensions.width}
          height={canvasDimensions.height}
          className="max-w-full max-h-full"
        />
      )}

      {/* HUD */}
      <div id="minigame-hud" className="absolute top-4 left-4 flex flex-col gap-2 z-20">
        <div className="flex gap-2">
          {/* Lives display */}
          <div
            id="minigame-lives-display"
            className={`bg-black bg-opacity-60 text-white pl-5 pr-3 py-2 rounded-lg flex items-center gap-2 relative transition-transform duration-200 ${
              pulseType === "lives" ? "scale-125" : "scale-100"
            }`}
          >
            <div className="absolute -left-2 top-1/2 -translate-y-1/2">
              <img
                src="/images/icon-lives.png"
                alt="Lives"
                width={36}
                height={36}
                className="drop-shadow-lg"
                style={{ imageRendering: "pixelated" }}
              />
            </div>
            <span className="font-bold text-white ml-2 pl-2">+{hudRewards.lives}</span>
          </div>

          {/* Coins display */}
          <div
            id="minigame-coins-display"
            className={`bg-black bg-opacity-60 text-white pl-5 pr-3 py-2 rounded-lg flex items-center gap-2 relative transition-transform duration-200 ${
              pulseType === "coins" ? "scale-125" : "scale-100"
            }`}
          >
            <div className="absolute -left-2 top-1/2 -translate-y-1/2">
              <img
                src="/images/icon-coins.png"
                alt="Coins"
                width={36}
                height={36}
                className="drop-shadow-lg"
                style={{ imageRendering: "pixelated" }}
              />
            </div>
            <span className="font-bold text-white ml-2 pl-1.5">{hudRewards.coins}</span>
          </div>

          {/* Premium Towers display */}
          <div
            id="minigame-towers-display"
            className={`bg-black bg-opacity-60 text-white pl-5 pr-3 py-2 rounded-lg flex items-center gap-2 relative transition-transform duration-200 ${
              pulseType === "towers" ? "scale-125" : "scale-100"
            }`}
          >
            <div className="absolute -left-2 top-1/2 -translate-y-1/2">
              <img
                src="/images/icon-premiumtower.png"
                alt="Premium Towers"
                width={36}
                height={36}
                className="drop-shadow-lg"
                style={{ imageRendering: "pixelated" }}
              />
            </div>
            <span className="font-bold text-white ml-2 pl-1.5">{hudRewards.freePremiumTowers}</span>
          </div>
        </div>
      </div>

      {/* Bokeh effect overlay */}
      {bokehOpacity > 0 && (
        <div id="bokeh-overlay" className="absolute inset-0 pointer-events-none z-10" style={{ opacity: bokehOpacity }}>
          <svg width="100%" height="100%" className="absolute inset-0">
            <defs>
              <radialGradient id="bokeh1" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="white" stopOpacity="0.8" />
                <stop offset="100%" stopColor="white" stopOpacity="0" />
              </radialGradient>
              <radialGradient id="bokeh2" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="white" stopOpacity="0.6" />
                <stop offset="100%" stopColor="white" stopOpacity="0" />
              </radialGradient>
            </defs>

            {/* Large bokeh circles */}
            <circle cx="20%" cy="30%" r="80" fill="url(#bokeh1)" />
            <circle cx="80%" cy="20%" r="100" fill="url(#bokeh2)" />
            <circle cx="70%" cy="70%" r="120" fill="url(#bokeh1)" />
            <circle cx="30%" cy="80%" r="90" fill="url(#bokeh2)" />
            <circle cx="50%" cy="50%" r="60" fill="url(#bokeh1)" />
            <circle cx="15%" cy="60%" r="70" fill="url(#bokeh2)" />
            <circle cx="85%" cy="45%" r="85" fill="url(#bokeh1)" />

            {/* Speed lines from center */}
            {Array.from({ length: 12 }).map((_, i) => {
              const angle = (i * 30 * Math.PI) / 180
              const x1 = 50 + Math.cos(angle) * 20
              const y1 = 50 + Math.sin(angle) * 20
              const x2 = 50 + Math.cos(angle) * 60
              const y2 = 50 + Math.sin(angle) * 60
              return (
                <line
                  key={i}
                  x1={`${x1}%`}
                  y1={`${y1}%`}
                  x2={`${x2}%`}
                  y2={`${y2}%`}
                  stroke="white"
                  strokeWidth="2"
                  strokeOpacity="0.4"
                />
              )
            })}
          </svg>

          {/* White vignette */}
          <div
            className="absolute inset-0"
            style={{
              background: "radial-gradient(circle at center, transparent 30%, rgba(255,255,255,0.6) 100%)",
            }}
          />
        </div>
      )}

      {/* Loading overlay - shows if image loading takes > 200ms */}
      <LoadingOverlay
        progress={imageLoaded ? 100 : 50}
        isComplete={imageLoaded}
        minDelayMs={200}
      />
    </div>
  )
}
